import { PigUpdateMetadata } from "@hifieng/common/dist/types/graphql";
import { toCamelCase } from "./toCamelCase";

/* eslint-disable @typescript-eslint/camelcase */

//example pigUpdateMetadata array for reference
/*
const example = [
  { name: "Pig Point of Interest ETA", value: 1787251577 },
  { name: "Pig Receipt ETA", value: 1787365317 },
  { name: "Pig Notification Type", value: "30 Min Update" },
  { name: "Previous Valve Type", value: "Block Valve" },
  { name: "Upcoming Valve Type", value: "Block Valve" },
  { name: "Previous Point of Interest", value: "ET02-2MLV-0003" },
  { name: "Next Point of Interest", value: "2K18" },
  { name: "Pig Run ID Info", value: "NPS 36 Edmonton to Edson Cleaning Pig" },
  { name: "Pipeline Info", value: "tm_l2" },
  { name: "HPC Info", value: "Deployment L3" },
  { name: "Tool Type", value: "Cleaning Pig" }
];
*/

// map internal variable names to display names for UI
const PIG_METADATA_DISPLAY_NAME_MAPPING: Record<string, string> = {
  pigNotificationType: "Notification Type",
  speed: "Speed",
  pigPointOfInterestEta: "Next POI ETA",
  nextPointOfInterest: "Next POI",
  upcomingValveType: "Next POI Type",
  previousPointOfInterest: "Previous POI",
  previousValveType: "Previous POI Type",
  pigReceiptEta: "ETA at Receipt",
  toolType: "Tool Type",
  startTime: "Time at Current Location",
  endTime: "End Time",
  kp: "KP",
  coordinates: "Coordinates",
  channel: "Channel",
  pigRunIdInfo: "Pig Run ID Info",
  pigRunId: "Pig Run ID",
  pipelineInfo: "Pipeline Info",
  pigType: "Pig Type"
};

//cSpell:disable
// map input names(normalized) to standardized names for internal variables
const PIG_DATA_IMPORT_NAME_MAPPING: Record<string, string> = {
  pignotificationtype: "pigNotificationType",
  pignotifftype: "pigNotificationType",

  speed: "speed",

  pigpointofinteresteta: "pigPointOfInterestEta",
  pigpoieta: "pigPointOfInterestEta",

  nextpointofinterest: "nextPointOfInterest",
  pignextpoi: "nextPointOfInterest",

  upcomingvalvetype: "upcomingValveType",
  pignextvalve: "upcomingValveType",

  previouspointofinterest: "previousPointOfInterest",
  pigprevpoi: "previousPointOfInterest",

  previousvalvetype: "previousValveType",
  pigprevvalve: "previousValveType",

  pigreceipteta: "pigReceiptEta",
  pigreceta: "pigReceiptEta",

  tooltype: "pigType",
  pigtool: "pigType",
  pigtype: "pigType",

  pigrunidinfo: "pigRunIdInfo",
  pigrunid: "pigRunId",

  pipelineinfo: "pipelineInfo",

  hpcname: "hpcName",
  loadname: "hpcName",
  hpcinfo: "hpcName"
};

type PigMetadataValueTransformType = {
  keyMatches: (key: string) => boolean;
  valueMap: Record<string, string>;
};

// Config-driven value transformations for pig metadata rows.
// Each entry checks if the row's key satisfies `keyMatches`, and if so,
// maps the row's value using `valueMap`.
const PIG_METADATA_VALUE_TRANSFORMS: Array<PigMetadataValueTransformType> = [
  {
    keyMatches: (key: string) => key.toLowerCase().includes("valve"),
    valueMap: {
      Check: "Check Valve",
      "Block (System)": "Block Valve"
    }
  },
  {
    keyMatches: (key: string) => key.toLowerCase().includes("pipelineinfo"),
    valueMap: {
      tm_l2: "Line 2",
      tm_l1: "Line 1"
    }
  }
];

function applyPigMetadataValueTransform(key: string, value: string): string {
  const transform = PIG_METADATA_VALUE_TRANSFORMS.find(entry =>
    entry.keyMatches(key)
  );
  if (!transform) return value;
  return transform.valueMap[value] || value;
}
//cSpell:enable

// Manual display order (case-insensitive)
// using internal variable names for consistency
const PIG_METADATA_MANUAL_ORDER: Array<string> = [
  "startTime",
  "kp",
  "coordinates",
  "pigNotificationType",
  "status",
  "speed",
  "pigPointOfInterestEta",
  "nextPointOfInterest",
  "upcomingValveType",
  "previousPointOfInterest",
  "previousValveType",
  "pigReceiptEta",
  "pigRunIdInfo",
  "pigRunId",
  "channel",
  "pipelineInfo"
];

// Add internal keys here to show those pig metadata rows only in admin section.
// Any key not listed remains visible to all users.
const PIG_METADATA_ADMIN_ONLY_KEYS = new Set<string>(["hpcName"]);

export type ParsePigUpdateMetadataInput = {
  pigUpdateMetadata?: Array<PigUpdateMetadata> | null;
  channel: string;
  startTime: number;
  kp: number;
  coordinates: string;
  pigRunId?: string | null;
  endTime?: number | null;
  weldId?: string | null;
  speed?: number | null;
  nextPoi?: string | null;
  arrivalEstimate?: number | null;
};

export type PigMetadataRow = {
  name: string;
  value: string;
  isTimestamp: boolean;
  originalKey: string;
  isKp: boolean;
  isCoordinates: boolean;
};

export type ParsedPigUpdateMetadata = {
  rows: Array<PigMetadataRow>;
  hasNextPoiEta: boolean;
  hasNextPoi: boolean;
  hasSpeed: boolean;
};

export function isPigMetadataAdminOnly(originalKey: string): boolean {
  return PIG_METADATA_ADMIN_ONLY_KEYS.has(originalKey);
}

const PIG_METADATA_ORDER_INDEX = new Map(
  PIG_METADATA_MANUAL_ORDER.map((name, i) => [name.toLowerCase(), i])
);

function getPigMetadataOrder(originalKey: string): number {
  const key = originalKey.trim().toLowerCase();
  const order = PIG_METADATA_ORDER_INDEX.get(key);

  return order === undefined ? Number.MAX_SAFE_INTEGER : order;
}

function normalizeImportName(rawName: string): string {
  return rawName
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function normalizePigMetadataKey(rawName: string): string {
  const compact = rawName.trim().replace(/\s+/g, " ");
  const normalized = normalizeImportName(compact);

  return (
    PIG_DATA_IMPORT_NAME_MAPPING[normalized] ||
    toCamelCase(compact.replace(/_/g, " "))
  );
}

//converts PascalCase or camelCase or snake_case to Title Case with spaces
function toDisplayName(key: string): string {
  if (PIG_METADATA_DISPLAY_NAME_MAPPING[key]) {
    return PIG_METADATA_DISPLAY_NAME_MAPPING[key];
  }

  const words =
    key.match(/[A-Z]+(?=[A-Z][a-z])|[A-Z]?[a-z0-9]+|[A-Z]+|_/g) || [];

  return words
    .filter(word => word !== "_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Validates if a string is a valid Unix timestamp in seconds (10 digits).
 * Unix timestamps should be in seconds, not milliseconds.
 * @param {string | null | undefined} timestampStr - The timestamp string to validate
 * @returns {boolean} True if valid 10-digit Unix timestamp, false otherwise
 */
export function isUnixTimestampString(
  timestampStr: string | null | undefined
): boolean {
  // console.log("isUnixTimestampString", timestampStr);
  if (!timestampStr) {
    return false;
  }
  const trimmed = timestampStr.trim();

  // This component converts the value from seconds to milliseconds,
  // so only accept second-based Unix timestamps.
  // checks for 10 digit UNIX timestamps, which is seconds.
  if (!/^\d{10}$/.test(trimmed)) {
    return false;
  }
  const timestamp = Number(trimmed);

  return Number.isInteger(timestamp) && timestamp > 0;
}

function createPigMetadataRow(
  key: string,
  value: string | number
): PigMetadataRow {
  const valueString = String(value).trim();

  return {
    name: toDisplayName(key),
    value: valueString,
    isTimestamp: isUnixTimestampString(valueString),
    originalKey: key,
    isKp: key === "kp",
    isCoordinates: key === "coordinates"
  };
}

function updatePigMetadataFlags(
  result: ParsedPigUpdateMetadata,
  key: string
): void {
  if (key === "pigPointOfInterestEta") result.hasNextPoiEta = true;
  if (key === "nextPointOfInterest") result.hasNextPoi = true;
  if (key === "speed") result.hasSpeed = true;
}

/**
 * Extracts and concatenates the pigRunIdInfo and pipelineInfo values from
 * pig update metadata into a single display string.
 */
export function getPigRunIdAndPipelineInfoString(
  pigUpdateMetadata?: Array<PigUpdateMetadata> | null
): string {
  const source = pigUpdateMetadata || [];
  let pigRunIdInfoValue = "";
  let pipelineInfoValue = "";

  source.forEach(item => {
    if (!item || !item.name || item.value === null) return;

    const key = normalizePigMetadataKey(item.name);
    const normalizedValue = String(item.value).trim();
    if (!normalizedValue) return;

    if (key === "pigRunIdInfo") pigRunIdInfoValue = normalizedValue;
    if (key === "pipelineInfo") {
      pipelineInfoValue = applyPigMetadataValueTransform(key, normalizedValue);
    }
  });

  return [pigRunIdInfoValue, pipelineInfoValue]
    .filter(value => value !== "")
    .join(" - ");
}

/**
 * Parses pig update metadata array (and standard pig event info) into organized rows. For TransMountain.
 */
export function parsePigUpdateMetadata(
  input: ParsePigUpdateMetadataInput
): ParsedPigUpdateMetadata {
  const result: ParsedPigUpdateMetadata = {
    rows: [],
    hasNextPoiEta: false,
    hasNextPoi: false,
    hasSpeed: false
  };

  const source = input.pigUpdateMetadata || [];
  const rows: Array<PigMetadataRow> = [];
  const existingKeys = new Set<string>();

  source.forEach(item => {
    if (!item || !item.name || item.value === null) {
      return;
    }

    const key = normalizePigMetadataKey(item.name);

    if (existingKeys.has(key)) {
      return;
    }

    const normalizedValue = String(item.value).trim();

    if (!normalizedValue) {
      return;
    }

    const value = applyPigMetadataValueTransform(key, normalizedValue);

    updatePigMetadataFlags(result, key);
    rows.push(createPigMetadataRow(key, value));
    existingKeys.add(key);
  });

  const addRow = (key: string, value: string | number) => {
    const valueString = String(value).trim();

    if (!valueString || existingKeys.has(key)) {
      return;
    }

    rows.push(createPigMetadataRow(key, valueString));
    existingKeys.add(key);

    updatePigMetadataFlags(result, key);
    // if (key === "pigPointOfInterestEta") result.hasNextPoiEta = true;
    // if (key === "nextPointOfInterest") result.hasNextPoi = true;
    // if (key === "speed") result.hasSpeed = true;
  };

  const addOptionalRow = (key: string, value?: string | number | null) => {
    if (value === null || value === undefined || value === "") return;
    addRow(key, value);
  };

  // Required base fields
  addRow("channel", input.channel);
  addRow("startTime", input.startTime);
  addRow("kp", input.kp);
  addRow("coordinates", input.coordinates);

  // Optional fields
  addOptionalRow("pigRunId", input.pigRunId);
  addOptionalRow("endTime", input.endTime);
  addOptionalRow("weldId", input.weldId);
  addOptionalRow(
    "speed",
    input.speed !== null && input.speed !== undefined
      ? `${input.speed} m/second`
      : undefined
  );
  addOptionalRow("nextPointOfInterest", input.nextPoi);
  addOptionalRow("pigPointOfInterestEta", input.arrivalEstimate);

  rows.sort((a, b) => {
    const oa = getPigMetadataOrder(a.originalKey);
    const ob = getPigMetadataOrder(b.originalKey);
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name);
  });

  result.rows = rows;
  return result;
}

type PigRunUpdateWithMetadataType = {
  pigUpdateMetadata?: Array<PigUpdateMetadata> | null;
};

type PigRunWithMetadataType = {
  latest?: PigRunUpdateWithMetadataType | null;
  updates?: Array<PigRunUpdateWithMetadataType> | null;
};

function getPigRunMetadataName(
  update: PigRunUpdateWithMetadataType | null | undefined
): string {
  if (!update || !update.pigUpdateMetadata) {
    return "";
  }

  return getPigRunIdAndPipelineInfoString(update.pigUpdateMetadata);
}

export const tmPigName = (pigRun: PigRunWithMetadataType): string | false => {
  const latestName = getPigRunMetadataName(pigRun.latest);

  if (latestName) {
    return latestName;
  }

  const updates = pigRun.updates || [];

  for (const update of updates) {
    const updateName = getPigRunMetadataName(update);

    if (updateName) {
      return updateName;
    }
  }

  return false;
};

export function getToolTypeFromMetadata(
  updates?: Array<{
    pigUpdateMetadata?: Array<PigUpdateMetadata> | null;
  }> | null
): string | undefined {
  const source = updates || [];

  for (const update of source) {
    const metadata = update.pigUpdateMetadata || [];

    for (const item of metadata) {
      if (!item || !item.name || item.value === null) {
        continue;
      }

      if (normalizePigMetadataKey(item.name) === "pigType") {
        return String(item.value);
      }
    }
  }

  return undefined;
}
